// src/pages/ProjectDetailPage.tsx - Updated with simplified data upload integration
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Breadcrumbs,
  Link,
  TextField,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Paper,
  Divider,
  IconButton,
  Chip,
  Collapse,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Download as DownloadIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  Notes as NotesIcon,
  Label as TagIcon,
  Timeline as TimelineIcon,
  Storage as DataIcon,
  Description as ReportIcon,
  Analytics as AnalysisIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getProjectDetails, 
  updateProject, 
  deleteProject,
  addProjectNote,
  updateProjectNote,
  deleteProjectNote,
  addProjectTag,
  removeProjectTag,
} from '../utils/projectApi';
import { getProjectDatasets, deleteDataset } from '../utils/dataApi';
import { getProjectAnalyses, deleteAnalysis, getAnalysisResults } from '../utils/analysisApi';
import { CreateDatasetModal } from '../components/data/CreateDatasetModal';
import { CreateAnalysisModal } from 'components/analysis/CreateAnalysisModal';

interface Project {
  project_id: string;
  user_id: string;
  title: string;
  description?: string;
  status: 'draft' | 'completed';
  created_at: string;
  updated_at: string;
  progress: {
    data: boolean;
    analysis: boolean;
    report: boolean;
  };
  progress_percentage: number;
  analysis_type?: string;
  file_count?: number;
  data_size_bytes?: number;
  notes: Array<{
    note_id: string;
    text: string;
    author: string;
    created_at: string;
    updated_at: string;
  }>;
  tags: string[];
  timeline: Array<{
    event_id: string;
    action: string;
    timestamp: string;
    metadata: Record<string, any>;
  }>;
}

interface ToastMessage {
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Inline editing state
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Project management state
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Note management state
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Tag management state
  const [addTagModalOpen, setAddTagModalOpen] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);

  // Progress step expansion state
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  // Data state
  const [hasData, setHasData] = useState(false);
  const [dataset, setDataset] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [createDatasetModalOpen, setCreateDatasetModalOpen] = useState(false);
  const [deleteDatasetConfirmOpen, setDeleteDatasetConfirmOpen] = useState(false);
  const [deletingDataset, setDeletingDataset] = useState(false);

  // Analsyis state
  const [hasAnalysis, setHasAnalysis] = useState(false);
  const [analysis, setAnalysis] = useState<any>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [createAnalysisModalOpen, setCreateAnalysisModalOpen] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [analysisResultsDialogOpen, setAnalysisResultsDialogOpen] = useState(false);
  const [deleteAnalysisConfirmOpen, setDeleteAnalysisConfirmOpen] = useState(false);
  const [deletingAnalysis, setDeletingAnalysis] = useState(false);

  // Report state
  const [createReportModalOpen, setCreateReportModalOpen] = useState(false);

  // Fetch project details
  const fetchProjectData = useCallback(async () => {
    if (!projectId || !currentUser) return;

    try {
      setLoading(true);
      setError(null);

      const projectData = await getProjectDetails(projectId);
      
      // Adapt the project data structure to remove 'info' from progress
      const adaptedProject = {
        ...projectData.project,
        progress: {
          data: projectData.project.progress.data || false,
          analysis: projectData.project.progress.analysis || false,
          report: projectData.project.progress.report || false,
        }
      };
      
      setProject(adaptedProject);
      setTitleValue(adaptedProject.title);
      setDescriptionValue(adaptedProject.description || '');
      
      // Check for data after loading project
      checkForData();
      
    } catch (err) {
      console.error('Error fetching project data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId, currentUser]);

  // Check if project has any datasets
  const checkForData = async () => {
    if (!projectId) return;

    try {
      setDataLoading(true);
      const response = await getProjectDatasets(projectId);
      
      // For MVP, we only support one dataset per project
      const projectDataset = response.datasets.length > 0 ? response.datasets[0] : null;
      setDataset(projectDataset);
      
      // Consider project to have data if dataset exists with uploaded files
      const hasValidData = projectDataset && projectDataset.file_count > 0;
      setHasData(!!hasValidData);
      
      console.log('Dataset check:', { 
        hasDataset: !!projectDataset, 
        datasetName: projectDataset?.dataset_name,
        fileCount: projectDataset?.file_count || 0,
        totalSize: projectDataset?.total_size_bytes || 0,
        hasValidData 
      });
    } catch (err) {
      console.error('Error checking for data:', err);
      setHasData(false);
      showToast('Failed to check dataset status', 'error');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchProjectData();
      await checkForAnalysis();
    };
    loadData();
  }, [fetchProjectData]);

  const showToast = (message: string, severity: ToastMessage['severity']) => {
    setToast({ message, severity });
  };

  const handleDatasetCreated = async () => {
    setCreateDatasetModalOpen(false);
    
    try {
      // **NO DELAYS - immediate refresh after upload completes**
      await checkForData();
      await fetchProjectData();
      showToast('Dataset uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error refreshing after dataset creation:', error);
      showToast('Dataset uploaded but page refresh failed. Please reload manually.', 'warning');
    }
  };

  const handleAnalysisCreated = async () => {
    setCreateAnalysisModalOpen(false);
    
    try {
      await checkForAnalysis();
      await fetchProjectData();
      showToast('Analysis created successfully!', 'success');
    } catch (error) {
      console.error('Error refreshing after analysis creation:', error);
      showToast('Analysis created but page refresh failed. Please reload manually.', 'warning');
    }
  };
    
  const handleSaveTitle = async () => {
    if (!project || !titleValue.trim()) return;

    try {
      setSaving(true);
      
      const data = await updateProject(project.project_id, {
        title: titleValue.trim(),
        description: project.description
      });

      setProject(prev => prev ? {
        ...prev,
        title: data.project.title,
        updated_at: data.project.updated_at,
      } : null);

      setEditingTitle(false);
      showToast('Project title updated!', 'success');
      
    } catch (err) {
      console.error('Error updating title:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update title';
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!project) return;

    try {
      setSaving(true);
      
      const data = await updateProject(project.project_id, {
        title: project.title,
        description: descriptionValue.trim()
      });

      setProject(prev => prev ? {
        ...prev,
        description: data.project.description,
        updated_at: data.project.updated_at,
      } : null);

      setEditingDescription(false);
      showToast('Project description updated!', 'success');
      
    } catch (err) {
      console.error('Error updating description:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update description';
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!project) return;

    try {
      setDeletingProject(true);
      await deleteProject(project.project_id);
      
      setDeleteProjectDialogOpen(false);
      showToast('Project deleted successfully!', 'success');
      
      // Navigate back to projects after a short delay
      setTimeout(() => {
        navigate('/dashboard/projects');
      }, 1500);
      
    } catch (err) {
      console.error('Error deleting project:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete project';
      showToast(errorMessage, 'error');
    } finally {
      setDeletingProject(false);
    }
  };

  const handleAddNote = async () => {
    if (!project || !newNoteText.trim()) return;

    try {
      setAddingNote(true);
      await addProjectNote(project.project_id, {
        title: '',
        content: newNoteText.trim()
      });

      await fetchProjectData();
      
      setAddNoteModalOpen(false);
      setNewNoteText('');
      showToast('Note added successfully!', 'success');
      
    } catch (err) {
      console.error('Error adding note:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to add note';
      showToast(errorMessage, 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleEditNote = (noteId: string, currentText: string) => {
    setEditingNoteId(noteId);
    setEditingNoteText(currentText);
  };

  const handleSaveNote = async (noteId: string) => {
    if (!project || !editingNoteText.trim()) return;

    try {
      await updateProjectNote(project.project_id, noteId, {
        title: '',
        content: editingNoteText.trim()
      });

      await fetchProjectData();
      setEditingNoteId(null);
      setEditingNoteText('');
      showToast('Note updated successfully!', 'success');
      
    } catch (err) {
      console.error('Error updating note:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update note';
      showToast(errorMessage, 'error');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!project) return;

    try {
      await deleteProjectNote(project.project_id, noteId);
      await fetchProjectData();
      showToast('Note deleted successfully!', 'success');
      
    } catch (err) {
      console.error('Error deleting note:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete note';
      showToast(errorMessage, 'error');
    }
  };

  const handleAddTag = async () => {
    if (!project || !newTag.trim()) return;

    try {
      setAddingTag(true);
      await addProjectTag(project.project_id, newTag.trim());

      await fetchProjectData();
      
      setAddTagModalOpen(false);
      setNewTag('');
      showToast('Tag added successfully!', 'success');
      
    } catch (err) {
      console.error('Error adding tag:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to add tag';
      showToast(errorMessage, 'error');
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    if (!project) return;

    try {
      await removeProjectTag(project.project_id, tag);
      
      await fetchProjectData();
      showToast('Tag removed successfully!', 'success');
      
    } catch (err) {
      console.error('Error removing tag:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove tag';
      showToast(errorMessage, 'error');
    }
  };

  const handleStepClick = (step: string) => {
    if (step === 'data') {
      if (hasData) {
        // If has data, toggle expanded view
        setExpandedStep(expandedStep === step ? null : step);
      } else {
        // If no data, open create dataset modal
        setCreateDatasetModalOpen(true);
      }
    } else if (step === 'analysis') {
      if (hasAnalysis) {
        // If has analysis, toggle expanded view
        setExpandedStep(expandedStep === step ? null : step);
      } else {
        // If no analysis, open create analysis modal
        setCreateAnalysisModalOpen(true);
      }
    } else {
      // For report, just show coming soon for now
      setExpandedStep(expandedStep === step ? null : step);
    }
  };

  const getProgressStepIcon = (step: string) => {
    switch (step) {
      case 'data': return <DataIcon />;
      case 'analysis': return <AnalysisIcon />;
      case 'report': return <ReportIcon />;
      default: return <DataIcon />;
    }
  };

  const getProgressStepDescription = (step: string) => {
    switch (step) {
      case 'data': 
        return hasData ? 'View and manage your uploaded dataset' : 'Upload your ecological data files';
      case 'analysis': 
        return hasAnalysis ? 
          (analysis?.status === 'completed' ? 'View results and manage your completed analysis' : 'Monitor your running analysis') : 
          'Configure and run your analysis';
      case 'report': 
        return 'Generate and customize your reports';
      default: 
        return '';
    }
  };

  const getStepButtonText = (step: string) => {
    switch (step) {
      case 'data': 
        return hasData ? 'View Dataset' : 'Upload Dataset';
      case 'analysis': 
        return hasAnalysis ? 
          (analysis?.status === 'completed' ? 'View Results' : 'View Analysis') : 
          'Configure Analysis';
      case 'report': 
        return 'Generate Report';
      default: 
        return 'Configure';
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getValidationStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <CheckIcon />;
      case 'invalid':
        return <ErrorIcon />;
      case 'partial':
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getValidationStatusLabel = (status: string) => {
    switch (status) {
      case 'valid':
        return 'Valid';
      case 'invalid':
        return 'Invalid';
      case 'partial':
        return 'Partial';
      default:
        return 'Pending';
    }
  };

  const getValidationStatusColor = (status: string) => {
    switch (status) {
      case 'valid':
        return 'success';
      case 'invalid':
        return 'error';
      case 'partial':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Dataset deletion handler - simplified
  const handleDeleteDataset = async () => {
    if (!dataset) return;

    try {
      setDeletingDataset(true); // Start loading state
      
      await deleteDataset(dataset.data_id);
      
      // Close the modal first
      setDeleteDatasetConfirmOpen(false);
      
      // Refresh data and project state
      await checkForData(); // Refresh dataset status
      await checkForAnalysis(); // Refresh analysis status (should be deleted)
      await fetchProjectData(); // Refresh project metadata and timeline
      
      showToast('Dataset and associated analysis deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting dataset:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete dataset';
      showToast(errorMessage, 'error');
    } finally {
      setDeletingDataset(false); // End loading state
    }
  };

  const checkForAnalysis = async () => {
    console.log('?? PROJECT DEBUG: checkForAnalysis called for project:', projectId);
    
    if (!projectId) return;

    try {
      setAnalysisLoading(true);
      const response = await getProjectAnalyses(projectId);
      console.log('?? PROJECT DEBUG: getProjectAnalyses response:', response);
      
      const projectAnalysis = response.analyses.length > 0 ? response.analyses[0] : null;
      console.log('?? PROJECT DEBUG: Analysis status from API:', projectAnalysis?.status);
      setAnalysis(projectAnalysis);
      setHasAnalysis(!!projectAnalysis);
    } catch (err) {
      console.error('?? PROJECT DEBUG: Error checking for analysis:', err);
      setHasAnalysis(false);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleDeleteAnalysis = async () => {
    if (!analysis) return;

    try {
      setDeletingAnalysis(true); // Start loading state
      
      await deleteAnalysis(analysis.analysis_id);
      
      // Close the modal first
      setDeleteAnalysisConfirmOpen(false);
      
      // Refresh analysis and project state
      await checkForAnalysis(); // Refresh analysis status (should be deleted)
      await fetchProjectData(); // Refresh project metadata and timeline
      
      showToast('Analysis deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting analysis:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete analysis';
      showToast(errorMessage, 'error');
    } finally {
      setDeletingAnalysis(false); // End loading state
    }
  };

  const handleViewAnalysisResults = async () => {
    // console.log('?? PROJECT DEBUG: handleViewAnalysisResults called');
    // console.log('?? PROJECT DEBUG: Current analysis:', analysis);
    // console.log('?? PROJECT DEBUG: Call stack:', new Error().stack);
    
    if (!analysis) return;
    
    try {
      const response = await getAnalysisResults(analysis.analysis_id, true);
      // console.log('?? PROJECT DEBUG: getAnalysisResults response:', response);
      setAnalysisResults(response.results);
      setAnalysisResultsDialogOpen(true);
    } catch (err) {
      // console.error('?? PROJECT DEBUG: Error loading analysis results:', err);
      showToast('Failed to load analysis results', 'error');
    }
  };

  const getAnalysisStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckIcon />;
      case 'failed': return <ErrorIcon />;
      case 'running':
      case 'queued': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  const getAnalysisStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getAnalysisStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running':
      case 'queued': return 'warning';
      default: return 'default';
    }
  };

  const getAnalysisStatusMessage = (analysis: any): string => {
    switch (analysis.status) {
      case 'completed': return 'Analysis completed successfully';
      case 'failed': return `Analysis failed${analysis.error_message ? ': ' + analysis.error_message : ''}`;
      case 'running': return `Analysis in progress... ${analysis.progress_percentage.toFixed(1)}% complete`;
      case 'queued': return 'Analysis queued for execution';
      default: return `Analysis status: ${analysis.status}`;
    }
  };

  const renderStepContent = (step: string) => {
    switch (step) {
      case 'data':
        return (
          <Box p={3}>
            {hasData && dataset ? (
              // Enhanced dataset details display
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Dataset: {dataset.dataset_name}</Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDatasetConfirmOpen(true)}
                    >
                      Delete Dataset
                    </Button>
                  </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" gap={2}>
                  <DataIcon sx={{ mt: 0.5, color: 'primary.main' }} />
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {dataset.dataset_name}
                      </Typography>
                      <Chip
                        icon={getValidationStatusIcon(dataset.validation_status)}
                        label={getValidationStatusLabel(dataset.validation_status)}
                        color={getValidationStatusColor(dataset.validation_status) as any}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Device:</strong> {dataset.data_type_or_device}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Files:</strong> {dataset.file_count} files - <strong>Size:</strong> {formatFileSize(dataset.total_size_bytes)}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Created:</strong> {new Date(dataset.created_at).toLocaleDateString()}
                      {dataset.updated_at !== dataset.created_at && (
                        <> - <strong>Updated:</strong> {new Date(dataset.updated_at).toLocaleDateString()}</>
                      )}
                    </Typography>

                    {/* Project Metadata Sync Display */}
                    {project && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Project Files:</strong> {project.file_count || 0} - <strong>Project Size:</strong> {formatFileSize(project.data_size_bytes || 0)}
                      </Typography>
                    )}

                    {dataset.validation_issues && dataset.validation_issues.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>{dataset.validation_issues.length} validation issue(s) found:</strong>
                        </Typography>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                          {dataset.validation_issues.slice(0, 3).map((issue: string, index: number) => (
                            <li key={index}>
                              <Typography variant="body2">{issue}</Typography>
                            </li>
                          ))}
                          {dataset.validation_issues.length > 3 && (
                            <li>
                              <Typography variant="body2">
                                ... and {dataset.validation_issues.length - 3} more
                              </Typography>
                            </li>
                          )}
                        </ul>
                      </Alert>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              // Enhanced upload prompt with disclaimer
              <Box textAlign="center" py={4}>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Dataset Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Upload your dataset to get started with analysis
                </Typography>
                
                {/* Upload Disclaimer */}
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Before You Upload
                  </Typography>
                  <Typography variant="body2">
                    Keep your browser window open during upload. Closing the window will cancel the upload.
                  </Typography>
                </Alert>
                
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => {
                    console.log('Upload button clicked, current state:', createDatasetModalOpen);
                    setCreateDatasetModalOpen(true);
                    console.log('After setState, should be true');
                  }}
                >
                  Upload Dataset
                </Button>
              </Box>
            )}

            <Dialog open={deleteDatasetConfirmOpen} onClose={() => setDeleteDatasetConfirmOpen(false)}>
              <DialogTitle>Delete Dataset</DialogTitle>
              <DialogContent>
                <Typography gutterBottom>
                  Are you sure you want to delete "{dataset?.dataset_name}"?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This will permanently delete all {dataset?.file_count || 0} files and cannot be undone.
                </Typography>
                
                {/* Analysis cascade warning */}
                {hasAnalysis && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Analysis Impact
                    </Typography>
                    <Typography variant="body2">
                      This will also delete your {analysis?.analysis_type?.toUpperCase() || 'analysis'} analysis 
                      {analysis?.status === 'completed' ? ' and all results' : ''} since it depends on this dataset.
                    </Typography>
                  </Alert>
                )}
                
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Warning:</strong> Deleting this dataset cannot be undone.
                  </Typography>
                </Alert>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDatasetConfirmOpen(false)} disabled={deletingDataset}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteDataset}
                  color="error"
                  variant="contained"
                  disabled={deletingDataset}
                >
                  {deletingDataset ? 'Deleting...' : 'Delete Dataset'}
                </Button>
              </DialogActions>
            </Dialog>

          </Box>
        );
      case 'analysis':
        return (
          <Box p={3}>

            {hasAnalysis && analysis ? (
              // Simple analysis summary view (matching data box style)
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Analysis: {analysis.analysis_type.toUpperCase()}</Typography>
                  <Box display="flex" gap={1}>
                    {analysis.status === 'completed' && (
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleViewAnalysisResults()}
                      >
                        Download Results
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteAnalysisConfirmOpen(true)}
                    >
                      Delete Analysis
                    </Button>
                  </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" gap={2}>
                  <AnalysisIcon sx={{ mt: 0.5, color: 'primary.main' }} />
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {analysis.analysis_type.toUpperCase()} Analysis
                      </Typography>
                      <Chip
                        icon={getAnalysisStatusIcon(analysis.status)}
                        label={getAnalysisStatusLabel(analysis.status)}
                        color={getAnalysisStatusColor(analysis.status) as any}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Analysis ID:</strong> {analysis.analysis_id}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Files Processed:</strong> {analysis.files_processed || 0} of {analysis.total_files || 0}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Created:</strong> {analysis.created_at ? new Date(analysis.created_at).toLocaleString() : 'N/A'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Started:</strong> {analysis.started_at ? new Date(analysis.started_at).toLocaleString() : 
                                                analysis.status !== 'configured' ? 'Job submitted' : 
                                                'In server queue, not started yet'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Completed:</strong> {analysis.completed_at ? new Date(analysis.completed_at).toLocaleString() : 'N/A'}
                    </Typography>

                    {/* Results Summary for completed analysis */}
                    {analysis.status === 'completed' && (
                      <Box mt={2}>
                        {analysisResults ? (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Results:</strong> {analysisResults.result_file_count} files - {formatFileSize(analysisResults.result_size_bytes)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Results:</strong> Loading...
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Show job ID if available */}
                    {analysis.cloud_job_id && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Job ID:</strong> {analysis.cloud_job_id}
                      </Typography>
                    )}

                    {/* Error message for failed analysis */}
                    {analysis.status === 'failed' && analysis.error_message && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Error:</strong> {analysis.error_message}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </Box>

                <Dialog open={deleteAnalysisConfirmOpen} onClose={() => setDeleteAnalysisConfirmOpen(false)}>
                  <DialogTitle>Delete Analysis</DialogTitle>
                  <DialogContent>
                    <Typography gutterBottom>
                      Are you sure you want to delete this {analysis?.analysis_type?.toUpperCase()} analysis?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This will permanently delete the analysis configuration 
                      {analysis?.status === 'completed' ? ' and all result files' : ''} and cannot be undone.
                    </Typography>
                    
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Warning:</strong> Deleting this analysis cannot be undone.
                      </Typography>
                    </Alert>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setDeleteAnalysisConfirmOpen(false)} disabled={deletingAnalysis}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleDeleteAnalysis}
                      color="error"
                      variant="contained"
                      disabled={deletingAnalysis}
                    >
                      {deletingAnalysis ? 'Deleting...' : 'Delete Analysis'}
                    </Button>
                  </DialogActions>
                </Dialog>

              </Box>
            ) : (
              // Enhanced analysis creation prompt (keep existing)
              <Box textAlign="center" py={4}>
                <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Analysis Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Configure and run analysis on your dataset
                </Typography>
                
                {/* Analysis Disclaimer */}
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    About Analysis Execution
                  </Typography>
                  <Typography variant="body2">
                    Analysis runs on cloud infrastructure and may take several minutes to hours. 
                    You can monitor progress and download results when complete.
                  </Typography>
                </Alert>
                
                <Button
                  variant="contained"
                  startIcon={<AssessmentIcon />}
                  onClick={() => setCreateAnalysisModalOpen(true)}
                >
                  Create Analysis
                </Button>
              </Box>
            )}

          </Box>
        );
      case 'report':
        return (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>Report Generation</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Generate comprehensive analysis reports
            </Typography>
            <Button variant="contained" disabled>
              Generate Report (Coming Soon)
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading project...
        </Typography>
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box>
        <Alert 
          severity="error" 
          action={
            <Box>
              <Button color="inherit" size="small" onClick={fetchProjectData} sx={{ mr: 1 }}>
                Retry
              </Button>
              <Button color="inherit" size="small" onClick={() => navigate('/dashboard/projects')}>
                Back to Projects
              </Button>
            </Box>
          }
        >
          {error || 'Project not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link 
          component={RouterLink} 
          to="/dashboard/projects" 
          color="inherit" 
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ArrowBackIcon sx={{ mr: 0.5, fontSize: 16 }} />
          My Projects
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
          {project.title}
        </Typography>
      </Breadcrumbs>

      {/* Project Header with Inline Editing */}
      <Box mb={4}>
        {/* Title */}
        <Box display="flex" alignItems="center" mb={2}>
          {editingTitle ? (
            <Box display="flex" alignItems="center" gap={1} flex={1}>
              <TextField
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                variant="outlined"
                size="small"
                autoFocus
                sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '2rem', fontWeight: 700 } }}
                disabled={saving}
              />
              <IconButton 
                onClick={handleSaveTitle} 
                disabled={saving || !titleValue.trim()}
                color="primary"
              >
                {saving ? <CircularProgress size={20} /> : <SaveIcon />}
              </IconButton>
              <IconButton 
                onClick={() => {
                  setEditingTitle(false);
                  setTitleValue(project.title);
                }}
                disabled={saving}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {project.title}
              </Typography>
              <IconButton onClick={() => setEditingTitle(true)} size="small">
                <EditIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={{ ml: 'auto' }}>
            <Chip 
              label={project.status === 'completed' ? 'Complete' : `${Math.round(project.progress_percentage)}% Complete`}
              color={project.status === 'completed' ? 'success' : 'warning'}
            />
          </Box>
        </Box>

        {/* Description */}
        <Box mb={2}>
          {editingDescription ? (
            <Box display="flex" alignItems="flex-start" gap={1}>
              <TextField
                value={descriptionValue}
                onChange={(e) => setDescriptionValue(e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={3}
                placeholder="Add a description..."
                sx={{ flex: 1 }}
                disabled={saving}
              />
              <Box display="flex" flexDirection="column" gap={1}>
                <IconButton 
                  onClick={handleSaveDescription} 
                  disabled={saving}
                  color="primary"
                  size="small"
                >
                  {saving ? <CircularProgress size={16} /> : <SaveIcon />}
                </IconButton>
                <IconButton 
                  onClick={() => {
                    setEditingDescription(false);
                    setDescriptionValue(project.description || '');
                  }}
                  disabled={saving}
                  size="small"
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ minHeight: '1.5rem', cursor: 'pointer' }}
                onClick={() => setEditingDescription(true)}
              >
                {project.description || 'Click to add a description...'}
              </Typography>
              <IconButton onClick={() => setEditingDescription(true)} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Created {new Date(project.created_at).toLocaleDateString()} - 
            Updated {new Date(project.updated_at).toLocaleDateString()}
          </Typography>
          <Button 
            color="error" 
            variant="outlined" 
            size="small"
            onClick={() => setDeleteProjectDialogOpen(true)}
          >
            Delete Project
          </Button>
        </Box>
      </Box>

      {/* Progress Overview - Updated to show as action buttons */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Project Steps
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(project.progress).map(([step, completed]) => {
            const isDataStep = step === 'data';
            const isAnalysisStep = step === 'analysis';
            const showExpanded = expandedStep === step && (
              (isDataStep && hasData) || 
              (isAnalysisStep && hasAnalysis) || 
              (!isDataStep && !isAnalysisStep)
            );
            
            return (
              <Grid item xs={12} sm={4} key={step}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderColor: completed ? 'success.main' : 'grey.300',
                    bgcolor: completed ? 'success.50' : 'grey.50',
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Box sx={{ color: completed ? 'success.main' : 'grey.500', mb: 1 }}>
                      {getProgressStepIcon(step)}
                    </Box>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize', fontWeight: 600, mb: 0.5 }}>
                      {step}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {getProgressStepDescription(step)}
                    </Typography>
                    
                    {/* Action Button */}
                    <Button
                      variant={completed ? "outlined" : "contained"}
                      size="small"
                      onClick={() => handleStepClick(step)}
                      disabled={
                        step === 'data' ? false : 
                        step === 'analysis' ? !project.progress.data :  // Enable analysis when data is done
                        step === 'report' ? !project.progress.analysis : // Enable report when analysis is done
                        false
                      }
                      startIcon={
                        isDataStep ? (
                          hasData ? <DataIcon /> : <CloudUploadIcon />
                        ) : isAnalysisStep ? (
                          hasAnalysis ? <AssessmentIcon /> : <AnalyticsIcon />
                        ) : <ReportIcon />
                      }
                      sx={{ mb: 1 }}
                    >
                      {getStepButtonText(step)}
                    </Button>
                    
                    {/* Show expand icon only if there's content to show */}
                    {((isDataStep && hasData) || 
                      (isAnalysisStep && hasAnalysis) || 
                      (step === 'report' && completed)) && (
                      <Box display="flex" justifyContent="center">
                        <IconButton 
                          size="small" 
                          onClick={() => handleStepClick(step)}
                        >
                          {showExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary" display="block">
                      {completed ? 'Complete' : 'Pending'}
                    </Typography>
                  </CardContent>
                  
                  {/* Expandable Content */}
                  <Collapse in={showExpanded}>
                    <Divider />
                    {renderStepContent(step)}
                  </Collapse>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Tabs for different sections */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Notes (${project.notes?.length || 0})`} icon={<NotesIcon />} iconPosition="start" />
          <Tab label={`Tags (${project.tags?.length || 0})`} icon={<TagIcon />} iconPosition="start" />
          <Tab label={`Timeline (${project.timeline?.length || 0})`} icon={<TimelineIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Project Notes
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddNoteModalOpen(true)}
            >
              Add Note
            </Button>
          </Box>

          {project.notes && project.notes.length > 0 ? (
           <Grid container spacing={2}>
             {project.notes.map((note) => (
               <Grid item xs={12} md={6} key={note.note_id}>
                 <Card>
                   <CardContent>
                     {editingNoteId === note.note_id ? (
                       <Box>
                         <TextField
                           value={editingNoteText}
                           onChange={(e) => setEditingNoteText(e.target.value)}
                           variant="outlined"
                           size="small"
                           multiline
                           rows={3}
                           fullWidth
                           sx={{ mb: 1 }}
                         />
                         <Box display="flex" gap={1}>
                           <Button 
                             size="small" 
                             onClick={() => handleSaveNote(note.note_id)}
                             disabled={!editingNoteText.trim()}
                           >
                             Save
                           </Button>
                           <Button 
                             size="small" 
                             onClick={() => {
                               setEditingNoteId(null);
                               setEditingNoteText('');
                             }}
                           >
                             Cancel
                           </Button>
                         </Box>
                       </Box>
                     ) : (
                       <Box>
                         <Typography variant="body1" sx={{ mb: 1 }}>
                           {note.text}
                         </Typography>
                         <Box display="flex" justifyContent="space-between" alignItems="center">
                           <Typography variant="caption" color="text.secondary">
                             By {note.author} - {new Date(note.created_at).toLocaleDateString()}
                           </Typography>
                           <Box>
                             <IconButton 
                               size="small" 
                               onClick={() => handleEditNote(note.note_id, note.text)}
                             >
                               <EditIcon fontSize="small" />
                             </IconButton>
                             <IconButton 
                               size="small" 
                               onClick={() => handleDeleteNote(note.note_id)}
                               color="error"
                             >
                               <DeleteIcon fontSize="small" />
                             </IconButton>
                           </Box>
                         </Box>
                       </Box>
                     )}
                   </CardContent>
                 </Card>
               </Grid>
             ))}
           </Grid>
         ) : (
           <Typography color="text.secondary">No notes yet. Add your first note to get started.</Typography>
         )}
       </Box>
     )}

     {activeTab === 1 && (
       <Box>
         <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
           <Typography variant="h6" sx={{ fontWeight: 600 }}>
             Project Tags
           </Typography>
           <Button
             variant="contained"
             startIcon={<AddIcon />}
             onClick={() => setAddTagModalOpen(true)}
           >
             Add Tag
           </Button>
         </Box>
         
         {project.tags && project.tags.length > 0 ? (
           <Box display="flex" flexWrap="wrap" gap={1}>
             {project.tags.map((tag) => (
               <Chip
                 key={tag}
                 label={tag}
                 onDelete={() => handleRemoveTag(tag)}
                 color="primary"
                 variant="outlined"
               />
             ))}
           </Box>
         ) : (
           <Typography color="text.secondary">No tags yet. Add tags to organize and categorize your project.</Typography>
         )}
       </Box>
     )}

     {activeTab === 2 && (
       <Box>
         <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
           Project Timeline
         </Typography>
         
         {project.timeline && project.timeline.length > 0 ? (
           <List>
             {project.timeline.map((event, index) => (
               <React.Fragment key={event.event_id}>
                 <ListItem>
                   <MuiListItemText
                     primary={event.action}
                     secondary={
                       <Box>
                         <Typography variant="caption">
                           {new Date(event.timestamp).toLocaleString()}
                         </Typography>
                         {event.metadata && Object.keys(event.metadata).length > 0 && (
                           <Typography variant="caption" color="text.secondary" display="block">
                             {JSON.stringify(event.metadata)}
                           </Typography>
                         )}
                       </Box>
                     }
                   />
                 </ListItem>
                 {index < project.timeline.length - 1 && <Divider />}
               </React.Fragment>
             ))}
           </List>
         ) : (
           <Typography color="text.secondary">No timeline events yet.</Typography>
         )}
       </Box>
     )}

     {/* Data Upload Modal */}
     <CreateDatasetModal
       open={createDatasetModalOpen}
       onClose={() => setCreateDatasetModalOpen(false)}
       projectId={projectId || ''}
       onDatasetCreated={handleDatasetCreated}
     />

     {/* Analysis Configuration Modal */}
      <CreateAnalysisModal
        open={createAnalysisModalOpen}
        onClose={() => setCreateAnalysisModalOpen(false)}
        projectId={projectId || ''}
        onAnalysisCreated={handleAnalysisCreated}
      />

      {/* FIXME: Report Configuration Modal goes here */}

     {/* Toast Notifications */}
     <Snackbar
       open={Boolean(toast)}
       autoHideDuration={6000}
       onClose={() => setToast(null)}
       anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
     >
       <Alert
         onClose={() => setToast(null)}
         severity={toast?.severity}
         sx={{ width: '100%' }}
       >
         {toast?.message}
       </Alert>
     </Snackbar>

     {/* Delete Project Confirmation Dialog */}
     <Dialog open={deleteProjectDialogOpen} onClose={() => !deletingProject && setDeleteProjectDialogOpen(false)}>
       <DialogTitle>Delete Project</DialogTitle>
       <DialogContent>
         <DialogContentText>
           Are you sure you want to delete "{project?.title}"? 
           This will delete all project data, notes, tags, and timeline events. This action cannot be undone and may take a few minutes depending on the size of your project. 
         </DialogContentText>
       </DialogContent>
       <DialogActions>
         <Button onClick={() => setDeleteProjectDialogOpen(false)} disabled={deletingProject}>
           Cancel
         </Button>
         <Button 
           onClick={handleDeleteProject}
           color="error"
           disabled={deletingProject}
           startIcon={deletingProject ? <CircularProgress size={16} /> : null}
         >
           {deletingProject ? 'Deleting...' : 'Delete Project'}
         </Button>
       </DialogActions>
     </Dialog>

     {/* Add Note Modal */}
     <Dialog open={addNoteModalOpen} onClose={() => setAddNoteModalOpen(false)} maxWidth="sm" fullWidth>
       <DialogTitle>Add Note</DialogTitle>
       <DialogContent>
         <TextField
           autoFocus
           margin="dense"
           label="Note Text"
           fullWidth
           multiline
           rows={4}
           variant="outlined"
           value={newNoteText}
           onChange={(e) => setNewNoteText(e.target.value)}
         />
       </DialogContent>
       <DialogActions>
         <Button onClick={() => setAddNoteModalOpen(false)} disabled={addingNote}>
           Cancel
         </Button>
         <Button 
           onClick={handleAddNote} 
           variant="contained" 
           disabled={addingNote || !newNoteText.trim()}
         >
           {addingNote ? <CircularProgress size={20} /> : 'Add Note'}
         </Button>
       </DialogActions>
     </Dialog>

     {/* Add Tag Modal */}
     <Dialog open={addTagModalOpen} onClose={() => setAddTagModalOpen(false)} maxWidth="sm" fullWidth>
       <DialogTitle>Add Tag</DialogTitle>
       <DialogContent>
         <TextField
           autoFocus
           margin="dense"
           label="Tag Name"
           fullWidth
           variant="outlined"
           value={newTag}
           onChange={(e) => setNewTag(e.target.value)}
         />
       </DialogContent>
       <DialogActions>
         <Button onClick={() => setAddTagModalOpen(false)} disabled={addingTag}>
           Cancel
         </Button>
         <Button 
           onClick={handleAddTag} 
           variant="contained" 
           disabled={addingTag || !newTag.trim()}
         >
           {addingTag ? <CircularProgress size={20} /> : 'Add Tag'}
         </Button>
       </DialogActions>
     </Dialog>

     <Dialog open={analysisResultsDialogOpen} onClose={() => setAnalysisResultsDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Analysis Results</DialogTitle>
      <DialogContent>
        {analysisResults ? (
          <Box>
            <Typography variant="body1" gutterBottom>
              <strong>Results Available:</strong> {analysisResults.result_file_count} files 
              ({formatFileSize(analysisResults.result_size_bytes)})
            </Typography>
            
            {analysisResults.download_urls && analysisResults.download_urls.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Download Files:
                </Typography>
                <List>
                  {analysisResults.download_urls.map((file: any, index: number) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <MuiListItemText
                        primary={file.filename}
                        secondary={formatFileSize(file.size_bytes)}
                      />
                      <Button
                        href={file.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                      >
                        Download
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        ) : (
          <Typography>Loading results...</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAnalysisResultsDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
     
   </Box>
 );
};

export default ProjectDetailPage;